import React from 'react';
import {
  Box, Typography, Grid, Chip, Divider, Stack,
} from '@mui/material';
import { AccessTime, Place, Person, SwapHoriz, SportsScore, Warning } from '@mui/icons-material';
import { format } from 'date-fns';

const StatRow = ({ label, v1, v2, max }) => {
  const total = max || 1;
  const pct1 = Math.round(((v1 || 0) / total) * 100);
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
        <Typography variant="caption" fontWeight="bold">{v1 ?? 0}</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" fontWeight="bold">{v2 ?? 0}</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.3 }}>
        <Box sx={{ flex: pct1 || 1, bgcolor: 'primary.main', height: 5, borderRadius: 1 }} />
        <Box sx={{ flex: 100 - pct1 || 1, bgcolor: 'secondary.main', height: 5, borderRadius: 1 }} />
      </Box>
    </Box>
  );
};

const StatisticsTab = ({ game }) => {
  if (!game) return null;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <Stack spacing={0.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTime fontSize="small" color="action" />
            <Typography variant="body2">
              {game.gameDate ? format(new Date(game.gameDate), 'dd MMM yyyy') : '-'} {game.startTime}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Place fontSize="small" color="action" />
            <Typography variant="body2">{game.venue?.name}, {game.venue?.city}</Typography>
          </Box>
          <Chip label={game.categorie?.replace(/_/g, ' ')} size="small" sx={{ width: 'fit-content' }} />
        </Stack>
      </Grid>

      <Grid item xs={12} sm={8}>
        <Typography variant="subtitle2" gutterBottom>Match Stats</Typography>
        <Stack spacing={1}>
          <StatRow label="Possession %" v1={game.possessionStats?.entity1} v2={game.possessionStats?.entity2} max={100} />
          <StatRow label="Passes" v1={game.passStats?.entity1?.total} v2={game.passStats?.entity2?.total}
            max={(game.passStats?.entity1?.total || 0) + (game.passStats?.entity2?.total || 0)} />
          <StatRow label="Shots" v1={game.shotsStats?.entity1?.total} v2={game.shotsStats?.entity2?.total}
            max={(game.shotsStats?.entity1?.total || 0) + (game.shotsStats?.entity2?.total || 0)} />
          <StatRow label="On Target" v1={game.shotsStats?.entity1?.onTarget} v2={game.shotsStats?.entity2?.onTarget}
            max={(game.shotsStats?.entity1?.onTarget || 0) + (game.shotsStats?.entity2?.onTarget || 0)} />
        </Stack>
      </Grid>

      {game.goals?.length > 0 && (
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" gutterBottom>Goals</Typography>
          <Stack spacing={0.5}>
            {game.goals.map((g, i) => (
              <Box key={g._id || i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">{g.minute}'</Typography>
                <SportsScore fontSize="small" color="success" />
                <Typography variant="body2">{g.scoredBy?.firstName} {g.scoredBy?.lastName}</Typography>
                <Chip label={g.type?.replace(/_/g, ' ')} size="small" />
                <Typography variant="caption" color="text.secondary">{g.entityId?.name}</Typography>
              </Box>
            ))}
          </Stack>
        </Grid>
      )}

      {game.change?.length > 0 && (
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" gutterBottom>Substitutions</Typography>
          <Stack spacing={0.5}>
            {game.change.map((c, i) => (
              <Box key={c._id || i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">{c.minute}'</Typography>
                <SwapHoriz fontSize="small" />
                <Typography variant="body2" color="error.main">↓ {c.out?.firstName} {c.out?.lastName}</Typography>
                <Typography variant="body2" color="success.main">↑ {c.in?.firstName} {c.in?.lastName}</Typography>
              </Box>
            ))}
          </Stack>
        </Grid>
      )}

      {game.referees?.length > 0 && (
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" gutterBottom>Referees</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {game.referees.map((r, i) => (
              <Chip key={r._id || i} icon={<Person />} size="small"
                label={`${r.userId?.firstName} ${r.userId?.lastName} (${r.type})`} />
            ))}
          </Stack>
        </Grid>
      )}

      {game.bestPlayer?.userId && (
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2">⭐ Best Player:</Typography>
            <Typography variant="body2">
              {game.bestPlayer.userId?.firstName} {game.bestPlayer.userId?.lastName}
              {game.bestPlayer.entityId && ` — ${game.bestPlayer.entityId?.name}`}
            </Typography>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default StatisticsTab;
